// ** Redux, Thunk & Root Reducer Imports
import thunk from "redux-thunk"
import createDebounce from "redux-debounced"
import rootReducer from "../reducers/rootReducer"
import { applyMiddleware, compose, createStore } from "redux"
// ** redux-persist wrappers
import { persistReducer, persistStore, createTransform } from "redux-persist"
// ** the local storage we'll be using to persist data
import AsyncStorage from "@react-native-async-storage/async-storage"
// ** redux-persist merge level
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2"
import { dropInlinePhotos, pruneSamByActivityId } from "./samPersistPruning"
import { syncSessionAcrossTabs } from "@data/auth/crossTabSession"
import { pageAddress } from "@common/navigation/liveAddress"
import { withLiveMemoryOnBoot } from "../reducers/liveMode/liveMemory"
import { persistedStaticData } from "../reducers/data/staticData"

// Lue au chargement, avant que l'app ne réécrive la barre d'adresse (App.js) : au web, c'est elle
// qui dit quel live cet onglet rouvre.
const bootAddress = pageAddress()

// Transform to blacklist large arrays in nested reducers to prevent localStorage quota exceeded
const dataBlacklistTransform = createTransform(
    // transform state on its way to being serialized and persisted
    (inboundState, key) => {
        if (key === "data") {
            return {
                ...inboundState,
                // L'abandon de l'attente de `tutor_ds` ne vaut que pour la séance.
                staticData: persistedStaticData(inboundState.staticData),
                // Reset backOffice large arrays to initial state
                backOffice: {
                    ...inboundState.backOffice,
                    liveReports: [],
                },
                // La semaine de « Ma dispo » ne se garde pas d'une ouverture de l'app à l'autre :
                // vieille de plusieurs jours, ou vide, elle s'affichait une seconde avant la vraie
                // (03/11). Sans elle, les écrans montrent leur squelette jusqu'à la semaine lue.
                tutorAvailability: {
                    ...inboundState.tutorAvailability,
                    weekView: null,
                    releasePreview: null,
                },
                // Reset tutors lists
                tutors: {
                    ...inboundState.tutors,
                    usersList: [],
                },
                // Ne PAS partager le funnelStateId du funnel été entre onglets :
                // redux-persist écrit dans localStorage (partagé sur la même
                // origine), donc un onglet hériterait de l'id du lead démarré dans
                // un AUTRE onglet → les écritures créneaux/matières partent sur le
                // mauvais lead (contamination cross-onglets, abos payés faux). On le
                // nulle à la persistance : chaque onglet repart d'un id propre (le
                // contexte sessionStorage par-onglet + startFunnel/resume le
                // rétablissent en mémoire dans l'onglet courant).
                summerFunnel: {
                    ...inboundState.summerFunnel,
                    funnelStateId: null,
                },
                // Même règle pour le funnel Stage (clone de l'été) : l'identité
                // de l'onglet ne doit JAMAIS fuiter via localStorage. En plus du
                // funnelStateId (un onglet neuf hériterait de l'id d'un AUTRE
                // lead → l'écran classe saute /start et toutes les réponses
                // partent sur le mauvais état serveur), le stage porte aussi en
                // redux initUser (email pré-rempli — la collecte d'identité
                // inline le réutiliserait pour un autre enfant) et templateSlug
                // (l'effet de sync écraserait le template de l'URL d'entrée par
                // celui d'un autre onglet). Chaque onglet repart propre : le
                // contexte sessionStorage par-onglet + /start ou /resume
                // rétablissent l'identité en mémoire dans l'onglet courant.
                stageFunnel: {
                    ...inboundState.stageFunnel,
                    funnelStateId: null,
                    userId: null,
                    initUser: null,
                    templateSlug: null,
                    profileKnown: false,
                    studentClass: null,
                },
                // Même règle pour le funnel Inscription : l'identité de l'onglet ne
                // doit jamais fuiter via localStorage. Le risque est ici plus
                // aigu qu'ailleurs — ce funnel passe la main à
                // create-subscription, donc un funnelStateId hérité d'un autre
                // onglet ferait porter la vente ET le bouclage `inscription_paid`
                // sur le mauvais lead.
                //
                // `userTimezone` rejoint la liste pour la même raison : c'est
                // un fuseau PAR LEAD (le fuseau du COMPTE que ce lead a déjà,
                // pas celui du device), donc le persister ferait annoncer à un
                // lead neuf, sur le même onglet, l'heure locale d'un AUTRE
                // lead — ce que START/RESUME corrige en mémoire
                // ({@see inscriptionFunnel} reducer) mais qu'un aller-retour en
                // storage n'a aucune raison de réintroduire.
                //
                // Le catalogue matières est rechargé à chaque visite : gardé,
                // il manquerait au rail une matière ouverte depuis.
                inscriptionFunnel: {
                    ...inboundState.inscriptionFunnel,
                    funnelStateId: null,
                    // Tirée PAR DOSSIER, comme l'identifiant qu'elle suit :
                    // persistée, un onglet rechargé afficherait la disposition
                    // du dernier dossier ouvert dans un autre.
                    formatStep: null,
                    userId: null,
                    initUser: null,
                    userTimezone: null,
                    subjects: null,
                    subjectsClass: null,
                },
                // Ne persister que les trois dernières activités jouées avec
                // Sam : le jour où le quota saute, ce n'est pas Sam qui tombe,
                // c'est toute la persistance de l'application.
                ...(inboundState.sam
                    ? {
                          sam: {
                              ...inboundState.sam,
                              conversationByActivityId: dropInlinePhotos(
                                  pruneSamByActivityId(
                                      inboundState.sam.conversationByActivityId
                                  )
                              ),
                              contextByActivityId: pruneSamByActivityId(
                                  inboundState.sam.contextByActivityId
                              ),
                          },
                      }
                    : {}),
            }
        }
        // Les signaux de débordement d'une salle ne valent que jusqu'au poll
        // suivant : persistés, un rechargement rejouerait un pop-up périmé.
        if (key === "liveMode") {
            return { ...inboundState, liveRoomSignals: null }
        }
        return inboundState
    },
    // transform state being rehydrated (no changes needed)
    (outboundState) => outboundState,
    // define which reducers this transform gets called for
    { whitelist: ["data", "liveMode"] }
)

// persist config
const persistConfig = {
    key: "root",
    storage: AsyncStorage,
    stateReconciler: autoMergeLevel2,
    // `intercom` is intentionally excluded: its JWT expires after 24h and a
    // persisted pendingOpen could silently auto-open the messenger on cold start,
    // so the session must always start fresh.
    // `precallMemory` doit survivre aux séances : c'est le souvenir du dernier
    // test matériel réussi sur cet appareil, et il ne sert qu'aux entrées
    // suivantes.
    whitelist: ["data", "camera", "liveMode", "precallMemory"],
    transforms: [dataBlacklistTransform],
}
// wrap persist API around root reducer and store
// Le live persisté ne se rouvre que le jour de l'entrée, et au web qu'à son adresse (`liveMemory`).
const persistedReducer = withLiveMemoryOnBoot(
    persistReducer(persistConfig, rootReducer),
    persistConfig.key,
    bootAddress
)

// ** init middleware
const middleware = [thunk, createDebounce()]

// ** Dev Tools
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

// ** CreateM store
const store = createStore(
    persistedReducer,
    {},
    composeEnhancers(applyMiddleware(...middleware))
)
const persistor = persistStore(store)
syncSessionAcrossTabs(store, persistConfig.key)

export { store, persistor }
